import React from 'react';
import { Select, SelectProps } from 'antd';

import { LeadModel } from '@/typings/models/Lead';
import classNames from 'classnames';
import styles from '@/components/Lead/index.less';
import LeadDropdownFilter from '@/components/Filters/LeadDropdownFilter';
import { F } from '@/utils/intl';
import { useDispatch } from 'umi';

const { Option } = Select;

// Fixed set of supported languages with their display names and flag emojis
const SUPPORTED_LOCALES = [
  { value: 'en-US', label: '🇺🇸 English' },
  { value: 'es-ES', label: '🇪🇸 Español' },
  { value: 'fr-FR', label: '🇫🇷 Français' },
  { value: 'de-DE', label: '🇩🇪 Deutsch' },
  { value: 'it-IT', label: '🇮🇹 Italiano' },
  { value: 'pt-PT', label: '🇵🇹 Português' },
  { value: 'nl-NL', label: '🇳🇱 Nederlands' },
  { value: 'ru-RU', label: '🇷🇺 Русский' },

  // 🆕 Central & Eastern Europe
  { value: 'sl-SI', label: '🇸🇮 Slovenščina' }, // Slovenia
  { value: 'hr-HR', label: '🇭🇷 Hrvatski' }, // Croatian
  { value: 'ro-RO', label: '🇷🇴 Română' }, // Romanian
  { value: 'cs-CZ', label: '🇨🇿 Čeština' }, // Czech
  { value: 'sk-SK', label: '🇸🇰 Slovenčina' }, // Slovak
  { value: 'sr-RS', label: '🇷🇸 Srpski' }, // Serbian

  // 🇪🇺 Additional major European languages
  { value: 'pl-PL', label: '🇵🇱 Polski' }, // Polish
  { value: 'hu-HU', label: '🇭🇺 Magyar' }, // Hungarian
  { value: 'sv-SE', label: '🇸🇪 Svenska' }, // Swedish

  // Not supported now for XXXL
  // { value: 'fi-FI', label: '🇫🇮 Suomi' },         // Finnish
  // { value: 'da-DK', label: '🇩🇰 Dansk' },         // Danish
  // { value: 'no-NO', label: '🇳🇴 Norsk' },         // Norwegian
  // { value: 'el-GR', label: '🇬🇷 Ελληνικά' },      // Greek
  // { value: 'tr-TR', label: '🇹🇷 Türkçe' }         // Turkish (technically transcontinental, but widely used)
].sort((a, b) => a.value.localeCompare(b.value));

const getLeadLocale = (lead: LeadModel) => lead?.customer?.locale;

const getLocaleDisplay = (locale: string) => {
  const localeInfo = SUPPORTED_LOCALES.find(l => l.value === locale);
  if (!localeInfo) return locale || '-';
  return localeInfo.label;
};

const LeadLocale = ({ lead, readonly, isTable, isCustomer, value, onChange, className, ...props }: {
  isTable?: boolean,
  lead?: LeadModel,
  readonly?: boolean,
  value?: string,
  className?: string,
  isCustomer?: boolean,
  onChange?: (locale: string) => void,
  selectProps?: SelectProps & { bordered?: boolean },
}) => {
  const leadLocale = getLeadLocale(lead) || value;
  const dispatch = useDispatch();
  const representation = leadLocale ? getLocaleDisplay(leadLocale) : ' - ';

  const onChangeLocale = (newLocale) => {
    const value = newLocale?.value || newLocale
    if (lead) {
      dispatch({
        type: 'leads/edit',
        payload: {
          'customer.locale': value,
          leadId: lead.id,
        },
      });
    }

    onChange && onChange(value);

  };
  // For read-only display
  if (readonly || (!lead && !onChange)) {
    return (
      <span className={classNames(styles.leadLocale, {
        [styles.leadLocaleTable]: isTable,
      })}>
        {representation}
      </span>
    );
  }

  return (
    <LeadDropdownFilter
      filterKey={'customer.locale'}
      value={leadLocale}
      placeholder={
        <F
          id={'pages.leads.filters.locale.placeholder'}
          defaultMessage={'Language'}
        />
      }
      options={SUPPORTED_LOCALES}
      onChange={onChangeLocale}
      selectProps={{ bordered: false, allowClear: false, size: isTable ? 'medium' : 'small', ...props?.selectProps }}
      filterMode={'select'}
      className={classNames(styles.leadLocaleContainer, className, {
        [styles.leadLocaleTable]: isTable,
      })}
      {...props}
      selectProps={{
        bordered: false,
        allowClear: false,
        size: isTable ? 'medium' : 'small',
        ...(props?.selectProps || {}),
      }}
    />
  );
};

export default LeadLocale;
